import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { Avatar, Box, Button, ButtonBase, CircularProgress, FormControl, Grid, IconButton, InputAdornment, InputLabel, OutlinedInput, TextField, Typography } from '@mui/material';

import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { openSnackbar } from "../../store/slices/SnackbarSlice";
import { defaultRoles, login, stateProps } from '../../store/slices/AuthSlice';

import { LoginColumnStyle, LoginLayoutStyle, LoginRowStyle, LoginTextStyle } from '../../styles/LoginStyle';

import { EnviarCodigoVerificacion, Login, ValidarCodigoVerificacion } from '../../connections/auth/AuthConnection';

import '../../css/Login.css';

const MetodoEnvioStyle = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    p: 2,
    border: '1px solid #e0e0e0',
    borderRadius: 2,
    transition: 'border-color 0.2s, background-color 0.2s',
    '&:hover': {
        borderColor: 'primary.main',
        backgroundColor: 'rgba(0, 0, 0, 0.02)'
    },
    '&.Mui-disabled': {
        opacity: 0.5
    }
};

export const LoginPage = () => {

    const dispatch = useDispatch();

    const [showPassword, setShowPassword] = useState( false );
    const [loading, setLoading] = useState( false );
    const [loadingEnvio, setLoadingEnvio] = useState( false );

    const [correoElectronico, setCorreoElectronico] = useState( '' );
    const [contrasena, setContrasena] = useState( '' );

    const [paso, setPaso] = useState( 1 );
    const [correoEnmascarado, setCorreoEnmascarado] = useState( '' );
    const [telefonoEnmascarado, setTelefonoEnmascarado] = useState( '' );
    const [idTipoEnvio, setIdTipoEnvio] = useState( 0 );
    const [codigo, setCodigo] = useState( '' );
    const [segundosReenvio, setSegundosReenvio] = useState( 0 );

    const handleLogin = async ( e: React.MouseEvent<HTMLButtonElement, MouseEvent> ) => {

        e.preventDefault();

        const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(correoElectronico);

        if ( !correoElectronico ) {
            dispatch( openSnackbar({ message: 'Debes escribir el correo electrónico', variant: 'warning' }));
            return;
        }
        else if ( !validEmail ) {
            dispatch( openSnackbar({ message: 'El correo electrónico no tiene un formato validó', variant: 'warning' }));
            return;
        }
        else if ( !contrasena ) {
            dispatch( openSnackbar({ message: 'Debes escribir la contraseña', variant: 'warning' }));
            return;
        }
        else {

            setLoading( true );

            await Login( { correoElectronico, contrasena } ).then( resp => {

                setTimeout(() => {

                    const { success, message } = resp;

                    if ( success ) {

                        if ( resp.requiereVerificacion ) {

                            setCorreoEnmascarado( resp.correoElectronico ?? '' );
                            setTelefonoEnmascarado( resp.telefono ?? '' );
                            setPaso( 2 );

                            setLoading( false );

                            return;
                        }

                        const data: stateProps = {
                            nombres: resp?.nombres ?? '',
                            apellidos: resp?.apellidos ?? '',
                            token: resp?.token ?? '',
                            correoElectronico: correoElectronico,
                            administrador: resp.administrador ?? false,
                            coordinador: resp.coordinador ?? false,
                            restablecerContrasena: resp.restablecerContrasena ?? false,
                            idUnidad: resp.idUnidad ?? 0,
                            unidad: resp.unidad ?? '',
                            roles: resp.roles ?? defaultRoles,
                        };

                        dispatch( login( data ) );
                        window.localStorage.setItem('data', JSON.stringify(data));

                        setLoading( false );

                    }
                    else {

                        dispatch( openSnackbar({ message: message, variant: 'error' }));

                        setLoading( false );

                    }

                }, 700);

            });
        }
    }

    const handleEnviarCodigo = async ( tipoEnvio: number ) => {

        setIdTipoEnvio( tipoEnvio );
        setLoadingEnvio( true );

        await EnviarCodigoVerificacion( { correoElectronico, contrasena, idTipoEnvio: tipoEnvio } ).then( resp => {

            const { success, message } = resp;

            if ( success ) {

                dispatch( openSnackbar({ message: message, variant: 'success' }));

                setCodigo( '' );
                setSegundosReenvio( 60 );
                setPaso( 3 );

                setLoadingEnvio( false );

            }
            else {

                dispatch( openSnackbar({ message: message, variant: 'error' }));

                setLoadingEnvio( false );

            }

        });
    }

    const handleValidarCodigo = async ( e: React.MouseEvent<HTMLButtonElement, MouseEvent> ) => {

        e.preventDefault();

        if ( codigo.length !== 6 ) {
            dispatch( openSnackbar({ message: 'Debes escribir el código de 6 dígitos', variant: 'warning' }));
            return;
        }

        setLoading( true );

        await ValidarCodigoVerificacion( { correoElectronico, contrasena, codigo } ).then( resp => {

            setTimeout(() => {

                const { success, message } = resp;

                if ( success ) {

                    const data: stateProps = {
                        nombres: resp?.nombres ?? '',
                        apellidos: resp?.apellidos ?? '',
                        token: resp?.token ?? '',
                        correoElectronico: correoElectronico,
                        administrador: resp.administrador ?? false,
                        coordinador: resp.coordinador ?? false,
                        restablecerContrasena: resp.restablecerContrasena ?? false,
                        idUnidad: resp.idUnidad ?? 0,
                        unidad: resp.unidad ?? '',
                        roles: resp.roles ?? defaultRoles,
                    };

                    dispatch( login( data ) );
                    window.localStorage.setItem('data', JSON.stringify(data));

                    setLoading( false );

                }
                else {

                    dispatch( openSnackbar({ message: message, variant: 'error' }));

                    setLoading( false );

                }

            }, 700);

        });
    }

    const handleRegresar = () => {

        setPaso( 1 );
        setCodigo( '' );
        setIdTipoEnvio( 0 );
        setSegundosReenvio( 0 );

    }

    useEffect(() => {

        if ( segundosReenvio > 0 ) {

            const timer = setTimeout( () => setSegundosReenvio( segundosReenvio - 1 ), 1000 );

            return () => clearTimeout( timer );
        }

    }, [segundosReenvio])

    return (
        <Box style={ LoginLayoutStyle }>

            <Grid container style={ LoginRowStyle }>

                {/* Formulario */}
                <Grid size={{ xs: 12, md: 7, lg: 5 }} style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>

                    <Grid container style={ LoginColumnStyle }>

                        <Grid size={{ xs: 6 }} style={{ textAlign: 'center' }}>
                            <img src={ process.env.PUBLIC_URL + "/assets/logo-pjecz.png" } alt="Poder Judicial" style={{ height: 130 }} />
                        </Grid>

                        <Grid size={{ xs: 6 }} style={{ textAlign: 'center' }}>
                            <img src={ process.env.PUBLIC_URL + "/assets/logo.png" } alt="SAJI" style={{ height: 115 }} />
                        </Grid>

                    </Grid>

                    <Typography variant="h5" mt={ 2 } fontWeight={ 'bold' } textAlign={ 'center' } sx={{ mt: 5 }}>
                        { paso === 1 ? 'Bienvenidos' : 'Verificación de identidad' }
                    </Typography>

                    <Typography variant="subtitle1" color='secondary' fontSize={ 14 } mt={ 3 } textAlign={ 'center' }>
                        {
                            paso === 1
                            ?
                                'Ingrese con sus credenciales'
                            :
                                paso === 2
                                ?
                                    'Elige el método para recibir tu código de verificación'
                                :
                                    'Ingresa el código de 6 dígitos enviado a ' + ( idTipoEnvio === 1 ? correoEnmascarado : telefonoEnmascarado )
                        }
                    </Typography>

                    <form>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '350px',
                                gap: 2,
                                mt: 3,
                                mb: 3
                            }}
                        >

                            {
                                paso === 1
                                &&
                                    <>

                                        <FormControl sx={{ mt: 2 }}>

                                            <TextField
                                                fullWidth
                                                label='Correo electrónico'
                                                type="email"
                                                placeholder="persona@pjecz.gob.mx"
                                                autoComplete="off"
                                                variant="outlined"
                                                slotProps={{
                                                    input: {
                                                        autoComplete: 'off'
                                                    },
                                                    inputLabel: {
                                                        shrink: true,
                                                    }
                                                }}
                                                value={ correoElectronico }
                                                onChange={ (e) => setCorreoElectronico( e.target.value ) }
                                            />
                                        </FormControl>

                                        <FormControl sx={{ mt: 2 }}>
                                            <InputLabel shrink>Contraseña</InputLabel>
                                            <OutlinedInput
                                                notched
                                                fullWidth
                                                label='Contraseña'
                                                placeholder="****************"
                                                type={ showPassword ? 'text' : 'password' }
                                                value={ contrasena }
                                                onChange={ (e) => setContrasena( e.target.value ) }
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            edge="end"
                                                            size='small'
                                                            aria-label={ showPassword ? 'hide the password' : 'display the password' }
                                                            onClick={ () => setShowPassword( ( show ) => !show ) }
                                                        >
                                                            { showPassword ? <VisibilityOffIcon /> : <VisibilityIcon /> }
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                            />
                                        </FormControl>

                                        <Button
                                            fullWidth
                                            variant="contained"
                                            type='submit'
                                            sx={{ my: 3 }}
                                            onClick={ handleLogin }
                                            loading={ loading }
                                        >
                                            Ingresar
                                        </Button>

                                    </>
                            }

                            {
                                paso === 2
                                &&
                                    <>

                                        <ButtonBase
                                            sx={{ ...MetodoEnvioStyle, mt: 1 }}
                                            onClick={ () => handleEnviarCodigo( 1 ) }
                                            disabled={ loadingEnvio }
                                        >

                                            <Avatar sx={{ bgcolor: 'primary.main', width: 42, height: 42 }}>
                                                <EmailIcon fontSize='small' />
                                            </Avatar>

                                            <Box sx={{ flex: 1, textAlign: 'left' }}>
                                                <Typography fontSize={ 14 } fontWeight={ 600 }>Correo electrónico</Typography>
                                                <Typography fontSize={ 12 } color='textDisabled'>{ correoEnmascarado }</Typography>
                                            </Box>

                                            { loadingEnvio && idTipoEnvio === 1 ? <CircularProgress size={ 20 } /> : <ChevronRightIcon color='action' /> }

                                        </ButtonBase>

                                        <ButtonBase
                                            sx={ MetodoEnvioStyle }
                                            onClick={ () => handleEnviarCodigo( 2 ) }
                                            disabled={ loadingEnvio || !telefonoEnmascarado }
                                        >

                                            <Avatar sx={{ bgcolor: 'primary.main', width: 42, height: 42 }}>
                                                <SmsIcon fontSize='small' />
                                            </Avatar>

                                            <Box sx={{ flex: 1, textAlign: 'left' }}>
                                                <Typography fontSize={ 14 } fontWeight={ 600 }>SMS</Typography>
                                                <Typography fontSize={ 12 } color='textDisabled'>{ telefonoEnmascarado || 'Sin teléfono registrado' }</Typography>
                                            </Box>

                                            { loadingEnvio && idTipoEnvio === 2 ? <CircularProgress size={ 20 } /> : <ChevronRightIcon color='action' /> }

                                        </ButtonBase>

                                        <ButtonBase
                                            sx={ MetodoEnvioStyle }
                                            onClick={ () => handleEnviarCodigo( 3 ) }
                                            disabled={ loadingEnvio || !telefonoEnmascarado }
                                        >

                                            <Avatar sx={{ bgcolor: 'primary.main', width: 42, height: 42 }}>
                                                <WhatsAppIcon fontSize='small' />
                                            </Avatar>

                                            <Box sx={{ flex: 1, textAlign: 'left' }}>
                                                <Typography fontSize={ 14 } fontWeight={ 600 }>WhatsApp</Typography>
                                                <Typography fontSize={ 12 } color='textDisabled'>{ telefonoEnmascarado || 'Sin teléfono registrado' }</Typography>
                                            </Box>

                                            { loadingEnvio && idTipoEnvio === 3 ? <CircularProgress size={ 20 } /> : <ChevronRightIcon color='action' /> }

                                        </ButtonBase>

                                        {
                                            !telefonoEnmascarado
                                            &&
                                                <Typography variant="caption" color='textDisabled' textAlign={ 'center' }>
                                                    No tienes un teléfono registrado, contacta al administrador o al área de informática para recibir el código por SMS o WhatsApp.
                                                </Typography>
                                        }

                                        <Button
                                            fullWidth
                                            variant="text"
                                            sx={{ mb: 3 }}
                                            onClick={ handleRegresar }
                                            disabled={ loadingEnvio }
                                        >
                                            Regresar
                                        </Button>

                                    </>
                            }

                            {
                                paso === 3
                                &&
                                    <>

                                        <FormControl sx={{ mt: 2 }}>

                                            <TextField
                                                fullWidth
                                                label='Código de verificación'
                                                placeholder="______"
                                                autoComplete="off"
                                                variant="outlined"
                                                slotProps={{
                                                    input: {
                                                        autoComplete: 'off',
                                                        sx: { input: { textAlign: 'center', fontSize: 24, letterSpacing: 8 } }
                                                    },
                                                    inputLabel: {
                                                        shrink: true,
                                                    },
                                                    htmlInput: {
                                                        maxLength: 6,
                                                        inputMode: 'numeric'
                                                    }
                                                }}
                                                value={ codigo }
                                                onChange={ (e) => setCodigo( e.target.value.replace( /\D/g, '' ) ) }
                                            />
                                        </FormControl>

                                        <Button
                                            fullWidth
                                            variant="contained"
                                            type='submit'
                                            sx={{ mt: 3 }}
                                            onClick={ handleValidarCodigo }
                                            loading={ loading }
                                        >
                                            Validar código
                                        </Button>

                                        <Button
                                            fullWidth
                                            variant="text"
                                            onClick={ () => handleEnviarCodigo( idTipoEnvio ) }
                                            loading={ loadingEnvio }
                                            disabled={ segundosReenvio > 0 || loading }
                                        >
                                            { segundosReenvio > 0 ? 'Reenviar código (' + segundosReenvio + 's)' : 'Reenviar código' }
                                        </Button>

                                        <Button
                                            fullWidth
                                            variant="text"
                                            sx={{ mb: 3 }}
                                            onClick={ () => { setPaso( 2 ); setCodigo( '' ); setSegundosReenvio( 0 ); } }
                                            disabled={ loading || loadingEnvio }
                                        >
                                            Cambiar método de envío
                                        </Button>

                                    </>
                            }

                        </Box>

                    </form>

                    <Typography style={ LoginTextStyle }>
                        <strong>SAJI</strong> es una plataforma de gestión judicial de última generación, la cual permite optimizar los procesos enfocados en ofrecer a la ciudadanía una justicia pronta, expedita, innovadora, transparente y abierta.
                    </Typography>

                    <Typography variant="caption" color='textDisabled' textAlign={ 'center' } display={ 'block' }>
                        Versión { process.env.REACT_APP_VERSION }
                    </Typography>

                </Grid>

                {/* Imagen */}

                <Grid size={{ xs: 12, md: 5, lg: 7 }} sx={{ display: { md: 'flex', xs: 'none' } }} style={{ alignItems: "center", justifyContent: "center", background: "#f0f2f5", height: "100%" }}>
                    <img src={ process.env.PUBLIC_URL + "/assets/balanzabg.jpeg" } alt="Balanza" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </Grid>

            </Grid>

        </Box>
    );
};
